import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import FaqAccordion from "@/components/feature/FaqAccordion";
import { useSeo } from "@/hooks/useSeo";
import { site } from "@/mocks/site";
import { faqs } from "@/mocks/faq";

const voices = [
  {
    name: "投稿人 · 小林",
    role: "连续投稿 11 个月",
    text: "以前投稿最怕被断章取义。这里会先把时间线排好再发，反而比我原话讲得更清楚。",
  },
  {
    name: "老读者 · 阿哲",
    role: "来自社交平台推荐",
    text: "刷热搜是看热闹，来这边是看过程。同样的瓜，这边能读到前因后果，不用自己拼图。",
  },
  {
    name: "从业者 · 匿名",
    role: "内容行业从业者",
    text: "被整理过一条跟工作相关的线索，编辑在发布前专门来确认了两处措辞。这种克制挺少见。",
  },
];

export default function About() {
  useSeo({
    title: "关于我们 - 网曝吃瓜 | 内容原则、投稿规范与联系渠道",
    description:
      "了解网曝吃瓜的定位与发展理念：我们如何整理网友投稿、核对爆料线索、保护投稿人信息。包含内容原则、数据概览、读者口碑与编辑部联系方式。",
    keywords: "关于网曝吃瓜,投稿规范,内容原则,吃瓜线索核实,网友投稿平台",
    ogType: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于我们 - 网曝吃瓜",
        description: "网曝吃瓜的定位、内容原则与联系渠道。",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "网曝吃瓜",
        description: site.intro,
        email: site.email,
        foundingDate: site.since,
        slogan: site.slogan,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://wangbaochigua.example/" },
          { "@type": "ListItem", position: 2, name: "关于我们" },
        ],
      },
    ],
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="w-full px-4 md:px-6 py-8 md:py-12">
            <Breadcrumb items={[{ label: "关于我们" }]} />
            <h1 className="mt-5 text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
              我们是这样一个
              <span className="text-primary-600">整理线索的地方</span>
            </h1>
            <p className="mt-4 text-sm md:text-lg text-foreground-600 leading-relaxed max-w-3xl">{site.intro}</p>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-12 md:py-16 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-6">
            <p className="text-xs font-medium tracking-widest text-secondary-600 uppercase">网站定位</p>
            <h2 className="mt-3 text-2xl md:text-3xl font-bold text-foreground-950 leading-snug">
              不做第一时间的爆料者，做把过程讲清楚的整理者
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              很多爆料之所以变成争议，不是因为事实难找，而是因为信息被切碎了。网曝吃瓜做的事很朴素：
              把网友投稿按时间排好、标注来源、补上背景，让看到的人可以自己判断。
            </p>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              我们同时对「不做什么」有明确边界——不做人身攻击、不传播未经核实的指控、不公开投稿人的任何联系方式。
              这些边界写在内容原则里，也写在编辑部的日常工作流程里。
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "所有条目标注投稿时间与可核实程度",
                "涉及双方的内容尽量并列呈现不同说法",
                "被指出偏差时公开更正并保留修改记录",
              ].map((line) => (
                <li key={line} className="flex items-start gap-3 text-sm md:text-base text-foreground-700">
                  <i className="ri-check-line text-primary-500 mt-1 shrink-0" aria-hidden="true" />
                  {line}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6">
            <div className="w-full h-64 md:h-[380px] rounded-lg overflow-hidden bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20editorial%20illustration%20of%20an%20organized%20archive%20wall%20with%20pinned%20notes%20and%20connected%20lines%2C%20warm%20coral%20red%20and%20golden%20amber%20palette%2C%20minimal%20flat%20design%2C%20cream%20background%2C%20magazine%20clean%20composition%2C%20soft%20grain&width=1000&height=800&seq=gg-about-01&orientation=landscape"
                alt="网曝吃瓜编辑部线索整理工作的抽象插画"
                title="关于网曝吃瓜 - 线索整理流程"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 pb-12 md:pb-16">
          <div className="rounded-lg bg-foreground-950 p-6 md:p-10">
            <h2 className="text-2xl md:text-3xl font-bold text-background-50">内容原则</h2>
            <p className="mt-3 text-sm md:text-base text-background-300 leading-relaxed max-w-2xl">
              这四条不是口号，而是每条线索在发布前都要过的检查项。
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-8">
              {site.principles.map((item, idx) => (
                <div key={item.title} className="flex gap-4 p-5 rounded-lg bg-background-50/5 border border-background-50/10">
                  <span className="w-11 h-11 shrink-0 flex items-center justify-center rounded-md bg-accent-500 text-foreground-950">
                    <i className={`${item.icon} text-xl`} aria-hidden="true" />
                  </span>
                  <div>
                    <h3 className="text-base font-bold text-background-50">
                      {String(idx + 1).padStart(2, "0")} · {item.title}
                    </h3>
                    <p className="mt-1.5 text-sm text-background-300 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 pb-12 md:pb-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {site.stats.map((item) => (
              <div key={item.label} className="p-5 rounded-lg border border-background-200 bg-background-100">
                <p className="text-2xl md:text-3xl font-bold text-secondary-700">{item.value}</p>
                <p className="mt-2 text-xs md:text-sm text-foreground-600">{item.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section aria-labelledby="voice-title" className="w-full px-4 md:px-6 pb-12 md:pb-16">
          <div className="flex items-center gap-3 mb-6">
            <h2 id="voice-title" className="text-2xl md:text-3xl font-bold text-foreground-950 whitespace-nowrap">
              读者与投稿人怎么说
            </h2>
            <span className="flex-1 h-px rule-dashed" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {voices.map((v) => (
              <figure key={v.name} className="p-5 rounded-lg bg-accent-50 border border-accent-200 flex flex-col">
                <i className="ri-double-quotes-l text-2xl text-accent-600" aria-hidden="true" />
                <blockquote className="mt-3 text-sm md:text-base text-foreground-800 leading-relaxed flex-1">
                  {v.text}
                </blockquote>
                <figcaption className="mt-5 pt-4 border-t border-accent-200">
                  <p className="text-sm font-bold text-foreground-900">{v.name}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">{v.role}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section aria-labelledby="contact-title" className="w-full px-4 md:px-6 pb-12 md:pb-16">
          <div className="rounded-lg bg-secondary-900 p-6 md:p-10 grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2">
              <h2 id="contact-title" className="text-2xl md:text-3xl font-bold text-background-50">
                联系我们
              </h2>
              <p className="mt-3 text-sm md:text-base text-secondary-100 leading-relaxed max-w-2xl">
                投稿、纠错、申诉或合作，都可以通过下面的渠道找到编辑部。涉及内容修正的邮件请附上可核对的信息来源，
                我们会优先处理。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/submit"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-accent-500 text-foreground-950 text-sm whitespace-nowrap hover:bg-accent-400 transition-colors"
                >
                  <i className="ri-send-plane-fill" aria-hidden="true" />
                  在线投稿
                </Link>
                <a
                  href={`mailto:${site.email}`}
                  rel="nofollow"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-background-50/30 text-background-100 text-sm whitespace-nowrap hover:bg-background-50/10 transition-colors"
                >
                  <i className="ri-mail-line" aria-hidden="true" />
                  {site.email}
                </a>
              </div>
            </div>
            <dl className="flex flex-col gap-4 lg:border-l lg:border-secondary-700 lg:pl-8">
              <div>
                <dt className="text-xs text-secondary-300">投稿邮箱</dt>
                <dd className="mt-1 text-sm text-background-100 break-all">{site.tipEmail}</dd>
              </div>
              <div>
                <dt className="text-xs text-secondary-300">纠错与申诉</dt>
                <dd className="mt-1 text-sm text-background-100 break-all">{site.email}</dd>
              </div>
              <div>
                <dt className="text-xs text-secondary-300">响应时间</dt>
                <dd className="mt-1 text-sm text-background-100">工作日 24 小时内初筛</dd>
              </div>
            </dl>
          </div>
        </section>

        <div className="bg-background-100/60">
          <FaqAccordion
            items={faqs}
            idPrefix="about-faq"
            eyebrow="答疑"
            heading="投稿前，你可能还有这些疑问"
            intro="这些是编辑部收到最多的问题。如果还有别的顾虑，欢迎直接来信，我们会如实回复。"
          />
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}